import React, { useState, useRef } from 'react';
import { importPromptsFromJson } from '../utils/importService';
import { v4 as uuidv4 } from 'uuid';

/**
 * 备份导入组件
 * 支持从备份文件恢复数据
 */
const ImportPrompts = ({ onImport, folders, onAddFolder, tags, onAddTag }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // 处理文件选择
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      setImportError(null);
    }
  };

  // 处理文件导入
  const handleImport = async () => {
    if (!selectedFile) {
      setImportError('请先选择要导入的文件');
      return;
    }

    setIsImporting(true);
    setImportResult(null);
    setImportError(null);

    try {
      let result;
      
      // 检查文件类型
      if (selectedFile.name.endsWith('.json')) {
        result = await importPromptsFromJson(selectedFile);
      } else {
        throw new Error('不支持的文件格式，请选择JSON文件');
      }

      if (result.success) {
        // 处理导入的提示词
        const importedPrompts = await processImportedPrompts(result.prompts);
        
        // 更新导入结果
        setImportResult({
          count: importedPrompts.length,
          prompts: importedPrompts
        });
        
        // 调用导入回调
        if (onImport) {
          onImport(importedPrompts);
        }
      } else {
        setImportError(result.error || '导入失败');
      }
    } catch (error) {
      console.error('导入备份失败:', error);
      setImportError(error.message || '导入失败');
    } finally {
      setIsImporting(false);
    }
  };

  // 处理导入的提示词，创建必要的文件夹和标签
  const processImportedPrompts = async (prompts) => {
    // 创建文件夹名称到ID的映射
    const folderMap = {};
    folders.forEach(folder => {
      folderMap[folder.name.toLowerCase()] = folder.id;
    });

    // 创建标签名称到ID的映射
    const tagMap = {};
    tags.forEach(tag => {
      tagMap[tag.name.toLowerCase()] = tag.id;
    });

    // 记录本次导入中新创建的标签，避免在同一次导入中创建重复标签
    const newTagsInThisImport = new Map();

    // 处理每个提示词
    return prompts.map(prompt => {
      // 处理文件夹
      let folderId = prompt.folderId;
      
      // 如果有文件夹名称但没有有效的文件夹ID，根据名称处理
      if (prompt.folderName) {
        const folderNameLower = prompt.folderName.toLowerCase();
        if (folderMap[folderNameLower]) {
          // 使用现有文件夹
          folderId = folderMap[folderNameLower];
        } else {
          // 创建新文件夹
          const newFolderId = uuidv4();
          if (onAddFolder) {
            onAddFolder({
              id: newFolderId,
              name: prompt.folderName,
              createdAt: new Date().toISOString()
            });
          }
          folderMap[folderNameLower] = newFolderId;
          folderId = newFolderId;
        }
      }
      
      // 处理标签
      let tagIds = Array.isArray(prompt.tags) ? [...prompt.tags] : [];
      
      // 如果有标签名称，处理标签
      if (Array.isArray(prompt.tagNames) && prompt.tagNames.length > 0) {
        for (const tagName of prompt.tagNames) {
          if (!tagName) continue;
          
          const tagNameLower = tagName.toLowerCase();
          
          // 检查是否已存在相同名称的标签
          if (tagMap[tagNameLower]) {
            // 使用现有标签
            if (!tagIds.includes(tagMap[tagNameLower])) {
              tagIds.push(tagMap[tagNameLower]);
            }
          } else if (newTagsInThisImport.has(tagNameLower)) {
            // 使用本次导入中创建的标签
            if (!tagIds.includes(newTagsInThisImport.get(tagNameLower))) {
              tagIds.push(newTagsInThisImport.get(tagNameLower));
            }
          } else {
            // 创建新标签
            const newTagId = uuidv4();
            if (onAddTag) {
              onAddTag({
                id: newTagId,
                name: tagName,
                createdAt: new Date().toISOString()
              });
            }
            tagMap[tagNameLower] = newTagId;
            newTagsInThisImport.set(tagNameLower, newTagId);
            tagIds.push(newTagId);
          }
        }
      }
      
      // 返回处理后的提示词
      return {
        ...prompt,
        folderId,
        tags: tagIds
      };
    });
  };

  // 触发文件选择对话框
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="import-prompts">
      <h3 className="text-lg font-semibold mb-4">导入备份</h3>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">选择备份文件进行恢复。</p>
        <div className="flex items-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".json"
            className="hidden"
          />
          <button
            onClick={triggerFileInput}
            className="btn-modern-secondary mr-2"
            disabled={isImporting}
          >
            选择文件
          </button>
          {selectedFile && (
            <span className="text-sm">已选择: {selectedFile.name}</span>
          )}
        </div>
      </div>
      
      {selectedFile && (
        <div className="mb-4">
          <button
            onClick={handleImport}
            className="btn-modern-primary"
            disabled={isImporting}
          >
            {isImporting ? '导入中...' : '开始导入'}
          </button>
        </div>
      )}
      
      {importError && (
        <div className="text-red-500 mb-4">
          错误: {importError}
        </div>
      )}
      
      {importResult && (
        <div className="text-green-600 mb-4">
          成功恢复 {importResult.count} 个提示词
        </div>
      )}
      
      <div className="text-sm text-gray-600 mt-4">
        <h4 className="font-semibold mb-2">支持的导入格式:</h4>
        <ul className="list-disc pl-5">
          <li>JSON文件: 包含单个提示词或提示词数组的JSON文件</li>
        </ul>
      </div>
    </div>
  );
};

export default ImportPrompts;
